import { Linking } from "react-native";
import { openComposer } from "react-native-email-link";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { EquipSupportEmail, LogoutReason } from "@/constants/Login";
import { ProfileTestID } from "@/constants/TestID";
import useAuth from "@/hooks/useAuth";
import useHelpCenter from "@/hooks/useHelpCenter";
import { useAnalytics } from "@/services/Analytics";
import LegalFooterLinks, {
  FooterLinksLayout,
} from "@/ui/components/authentication/LegalFooterLinks";
import AppVersionLabel from "@/ui/components/common/AppVersionLabel";
import { Button, FAIconName } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import { isWeb } from "@/utils/platform.util";

interface ProfileFooterProps {
  selectedUserId: string;
}

const ProfileFooter = ({ selectedUserId }: ProfileFooterProps) => {
  const { track } = useAnalytics();
  const { logout } = useAuth();
  const { hideInAppNotifications } = useHelpCenter();
  const trackUrlOpened = (event: string) => {
    track(event, {
      [AnalyticsFieldName.Component]: ProfileFooter.name,
      [AnalyticsFieldName.SelectedUser]: selectedUserId,
      [AnalyticsFieldName.Source]: ProfileFooter.name,
    });
  };

  const onLogout = async () => {
    trackUrlOpened(ProfileAnalyticsEvent.Logout);
    // to prevent flickering and overlaying with the Loginpage,
    // messages must be hidden before routing to login page / logout hook
    await hideInAppNotifications();
    // "log out" will happen as follows:
    //  1) redirecting to the login page
    //  2) this unmounts all currently rendered components and associated RTKQuery queries
    //  3) the login page has a useEffect to invoke hooks/useOnlyFromLoginPageLogout, to do a full reset/proper logout
    await logout(LogoutReason.UserPressedLogout);
  };

  const handleContactSupport = () => {
    trackUrlOpened(ProfileAnalyticsEvent.ContactSupport);
    if (!isWeb()) {
      void openComposer({ to: EquipSupportEmail });
      return;
    }
    void Linking.openURL(`mailto:${EquipSupportEmail}`);
  };

  return (
    <VStack alignItems="center" gap={24}>
      <Button
        accessibilityLabel="Contact support"
        leftIcon={FAIconName.Envelope}
        onPress={handleContactSupport}
        size="small"
        testID={ProfileTestID.ContactSupport}
        title="Contact support"
        variant="secondary"
      />
      <HStack alignSelf="center" gap={FooterLinksLayout.spacing}>
        <LegalFooterLinks analyticsSource={ProfileFooter.name} />
        <Button
          accessibilityLabel="Log out"
          onPress={onLogout}
          size="small"
          testID={ProfileTestID.LogOut}
          title="Log out"
          variant="tertiary"
        />
      </HStack>
      <AppVersionLabel testID={ProfileTestID.Primary} />
    </VStack>
  );
};

export type { ProfileFooterProps };
export default ProfileFooter;
